/**
 * El tablero: lo primero que ve la oficina al entrar.
 *
 * 🚨 Rehecho el 21-ago-2026 sobre la maqueta que Luis aprobó. Sus dos reclamos,
 * los dos medidos antes de tocar nada:
 *
 *   «¿Cómo me vas a poner las alertas arriba para que todo el contenido esté
 *    abajo? ¿Que yo tenga que estar bajando la página para ver las cosas?»
 *        → el tablero medía 1.302 px de alto: 400 px de scroll.
 *
 *   «No hay gráficos, no hay información, no hay nada.»
 *        → cero gráficas en toda la consola.
 *
 * ⭐ Por eso las alertas se fueron a la **columna derecha** en vez de ocupar
 * todo el ancho arriba, y el centro lo ocupan las dos gráficas. Objetivo
 * medible: **que todo quepa en una pantalla de 900 px de alto, sin bajar.**
 *
 * Las gráficas son SVG escrito a mano. No se instala una librería de gráficas
 * para dibujar catorce barras: serían cientos de kilobytes para algo que la
 * oficina abre en un PC y que aquí son treinta líneas.
 */

import { quienEntro } from './Entrar.tsx'
import { comoFecha } from './formato.ts'

export interface DatosTablero {
  alertas: {
    rodandoSinManifiesto: number
    cargaSinLlegar: number
    porCorregir: number
    sinRevisar: number
    corregidasEsperando: number
    esperandoRespuesta: number
    esperaMasLargaHoras: number
    /** Camiones libres ahora. Va como contador del menú, no en el tablero. */
    camionesLibres: number
  }
  hoy: {
    entregado: number
    entregadoAyer: number
    viajesEnCalle: number
    conductoresEnCalle: number
    conductoresConApp: number
    conductoresEntregando: number
    manifiestosPorSubir: number
  }
  porDia: { dia: string; cuantas: number }[]
  flota: {
    enLaCalle: number
    yaSalieron: number
    aceptaronSinSalir: number
    sinAbrirLaApp: number
    devolviendo: number
  }
  recientes: {
    cuando: string
    tipo: string
    remesa: string
    conductor: string | null
    placa: string | null
  }[]
}

/** Cómo se le dice a cada movimiento, en palabras de la operación. */
const QUE_PASO: Record<string, { texto: string; tono: string }> = {
  ENTREGA_REGISTRADA: { texto: 'Entregó', tono: 'bien' },
  SALIDA_CARGUE: { texto: 'Salió del cargue', tono: 'normal' },
  ACEPTO_VIAJE: { texto: 'Arrancó el viaje', tono: 'normal' },
  DEVUELTA_A_PLANTA: { texto: 'Devolvió a planta', tono: 'aviso' },
  INTENTO_FALLIDO: { texto: 'No pudo entregar', tono: 'aviso' },
  CUMPLIDO_FISICO_ADJUNTO: { texto: 'Subió el cumplido', tono: 'bien' },
}

/** «hace 5 min», «hace 3 h», «ayer». Lo que se lee de un vistazo. */
/*
 * 🚨 Las fechas se parsean con `comoFecha`, NUNCA con `new Date()` a pelo.
 *
 * Postgres devuelve `2026-08-20 19:23:50.737-05` y JavaScript **no sabe leer
 * ese `-05`**: el estándar pide `-05:00`. La fecha queda inválida y la pantalla
 * dice «Nunca» sobre alguien que sí entró — se vio así, con una fila diciendo
 * «Nunca entró» y al lado «Entró, pero no ha entregado».
 *
 * ⛔ El error estaba en CINCO pantallas porque cada vez volví a escribir la
 * función en vez de usar la que ya existía en `formato.ts`.
 */
function haceCuanto(iso: string): string {
  const f = comoFecha(iso)
  if (!f) return ''
  const min = Math.round((Date.now() - f.getTime()) / 60000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.round(h / 24)
  return d === 1 ? 'ayer' : `hace ${d} días`
}

/**
 * Entregas por día de los últimos 14.
 *
 * ⭐ El fin de semana va en gris y el día de hoy en ámbar: sin eso, la caída
 * del sábado se lee como un desplome de la operación, y el día en curso —que
 * apenas va por la mitad— se lee como si hubiera sido un mal día.
 */
function GraficaDias({ datos }: { datos: { dia: string; cuantas: number }[] }) {
  if (datos.length === 0) return <p className="tab-vacio">Todavía no hay entregas registradas.</p>

  const IZQ = 34
  const DER = 8
  const BASE = 160
  const TOPE = 34
  const ancho = 700
  const slot = (ancho - IZQ - DER) / datos.length
  const barra = Math.min(26, slot - 12)

  const mayor = Math.max(...datos.map((d) => d.cuantas), 1)
  // Un techo redondo para que las líneas del fondo caigan en números legibles.
  const techo = Math.max(4, Math.ceil((mayor * 1.12) / 4) * 4)
  const alto = (v: number) => (v / techo) * (BASE - TOPE)

  const lineas = [0, techo / 4, techo / 2, (techo * 3) / 4, techo]

  return (
    <svg viewBox="0 0 700 200" width="100%" height={190} role="img"
      aria-label={`Entregas por día en los últimos ${datos.length} días`}>
      {lineas.map((v) => (
        <g key={v}>
          <line x1={IZQ} y1={BASE - alto(v)} x2={ancho - DER} y2={BASE - alto(v)}
            stroke="#E9ECF1" strokeWidth={1} />
          <text x={IZQ - 6} y={BASE - alto(v) + 3} fontSize={10} fill="#6B7789" textAnchor="end">
            {Math.round(v)}
          </text>
        </g>
      ))}

      {datos.map((d, i) => {
        const f = new Date(d.dia + 'T12:00:00')
        const finDeSemana = f.getDay() === 0 || f.getDay() === 6
        const esHoy = i === datos.length - 1
        const h = alto(d.cuantas)
        const x = IZQ + i * slot + (slot - barra) / 2
        return (
          <g key={d.dia}>
            <rect x={x} y={BASE - h} width={barra} height={Math.max(h, d.cuantas > 0 ? 2 : 0)}
              rx={2} fill={esHoy ? '#B4690E' : finDeSemana ? '#C9D2E0' : '#122A54'}>
              <title>{`${f.getDate()} · ${d.cuantas} entregas`}</title>
            </rect>
            {esHoy && d.cuantas > 0 && (
              <text x={x + barra / 2} y={BASE - h - 5} fontSize={11} fontWeight={700}
                fill="#B4690E" textAnchor="middle">
                {d.cuantas}
              </text>
            )}
            <text x={x + barra / 2} y={176} fontSize={10} textAnchor="middle"
              fill={esHoy ? '#B4690E' : '#6B7789'} fontWeight={esHoy ? 700 : 400}>
              {esHoy ? 'hoy' : f.getDate()}
            </text>
          </g>
        )
      })}

      <rect x={IZQ} y={188} width={9} height={9} rx={2} fill="#C9D2E0" />
      <text x={IZQ + 14} y={196} fontSize={10} fill="#6B7789">fin de semana</text>
      <rect x={IZQ + 106} y={188} width={9} height={9} rx={2} fill="#B4690E" />
      <text x={IZQ + 120} y={196} fontSize={10} fill="#6B7789">hoy, todavía en curso</text>
    </svg>
  )
}

/** Dónde está la flota ahora mismo, según lo que hizo el conductor en la app. */
function GraficaFlota({ f }: { f: DatosTablero['flota'] }) {
  const filas = [
    { et: 'Ya salieron del cargue', n: f.yaSalieron, color: '#122A54' },
    { et: 'Arrancaron, sin salir', n: f.aceptaronSinSalir, color: '#1890CC' },
    { et: 'Sin abrir la app', n: f.sinAbrirLaApp, color: '#C9D2E0' },
    { et: 'Devolviendo a planta', n: f.devolviendo, color: '#CA2B2B' },
  ]
  const mayor = Math.max(...filas.map((x) => x.n), 1)
  const X = 168
  const ANCHO = 470

  return (
    <svg viewBox="0 0 700 138" width="100%" height={132} role="img"
      aria-label="Dónde está la flota ahora">
      {filas.map((r, i) => {
        const y = 6 + i * 32
        return (
          <g key={r.et}>
            <text x={0} y={y + 12} fontSize={12.5} fill="#121B2B">{r.et}</text>
            <rect x={X} y={y} width={ANCHO} height={15} rx={3} fill="#EDEFF3" />
            <rect x={X} y={y} width={Math.max((r.n / mayor) * ANCHO, r.n > 0 ? 4 : 0)}
              height={15} rx={3} fill={r.color} />
            <text x={X + ANCHO + 10} y={y + 12} fontSize={12.5} fontWeight={700}
              fill={r.n > 0 && r.color === '#CA2B2B' ? '#CA2B2B' : '#121B2B'}>
              {r.n}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export function Tablero({
  datos,
  alIrA,
}: {
  datos: DatosTablero | null
  alIrA: (pestana: string) => void
}) {
  const yo = quienEntro()
  if (!datos) return <p className="cargando">Cargando…</p>

  const { alertas: a, hoy: h } = datos

  /*
   * 🚨 Las alertas, ordenadas por lo que cuesta si nadie las atiende. Solo se
   * pintan las que tienen algo: una fila en cero es ruido, y el ruido enseña a
   * ignorar los avisos.
   */
  const alertas = [
    {
      /*
        🚨🚨 VA DE PRIMERA: es la única en la que hay un camión QUIETO.
        
        Ayer agregué el número «Esperando respuesta» al tablero y **se me quedó
        por fuera de esta lista** — o sea que lo más urgente tenía número rojo y
        ningún botón para ir a resolverlo, mientras cosas menos graves sí lo
        tenían. Un número que no lleva a ninguna parte es un número que se mira
        y se olvida.
        
        El conductor llegó, no le recibieron, lo reportó, y la pelota quedó del
        lado de la oficina. Mientras nadie conteste, la mercancía está arriba
        del camión y el camión no se mueve. Eso es más caro que un papel que
        falta o que una entrega sin aprobar.
      */
      n: a.esperandoRespuesta,
      titulo:
        a.esperandoRespuesta === 1
          ? 'Conductor esperando respuesta'
          : 'Conductores esperando respuesta',
      dice:
        a.esperaMasLargaHoras >= 24
          ? `Llegaron y no les recibieron. Nadie les ha dicho qué hacer con la carga — el que más lleva, ${Math.floor(a.esperaMasLargaHoras / 24)} ${Math.floor(a.esperaMasLargaHoras / 24) === 1 ? 'día' : 'días'}. El camión está quieto.`
          : 'Llegaron y no les recibieron. Nadie les ha dicho qué hacer con la carga, y el camión está quieto.',
      nivel: 'grave',
      ir: 'entregas',
      boton: 'Contestarles',
    },
    {
      n: a.rodandoSinManifiesto,
      titulo: a.rodandoSinManifiesto === 1 ? 'Va rodando sin manifiesto' : 'Van rodando sin manifiesto',
      dice: 'Ya arrancaron y no llevan el papel. Si los paran en un retén, hay multa.',
      nivel: 'grave',
      ir: 'manifiestos',
      boton: 'Subirlos',
    },
    {
      n: a.cargaSinLlegar,
      titulo: a.cargaSinLlegar === 1 ? 'Carga sin llegar a planta' : 'Cargas sin llegar a planta',
      dice: 'El conductor dijo que la devolvía y no ha confirmado. Esa mercancía no está ubicada.',
      nivel: 'grave',
      ir: 'entregas',
      boton: 'Ver',
    },
    {
      n: a.porCorregir,
      titulo: a.porCorregir === 1 ? 'Entrega devuelta al conductor' : 'Entregas devueltas al conductor',
      dice: 'La oficina no aceptó la prueba. El cliente sigue sin su constancia.',
      nivel: 'media',
      ir: 'entregas',
      boton: 'Ver',
    },
    {
      n: a.sinRevisar,
      /* Se llama igual que el filtro al que manda: la pantalla dice «Falta
         aprobarla», así que decir «sin revisar» era mandarlo a buscar otra
         cosa. Ver `docs/decisiones.md`, 24-ago. */
      titulo: a.sinRevisar === 1 ? 'Entrega sin aprobar' : 'Entregas sin aprobar',
      /* Las corregidas se nombran aparte: el cliente lleva esperando desde la
         primera vez, no desde ahora. */
      dice:
        a.corregidasEsperando > 0
          ? `Llegaron con su foto y su firma, y nadie las ha aprobado. ${a.corregidasEsperando} ya vinieron corregidas: el conductor volvió donde el cliente y el cumplido sigue sin salir.`
          : 'Llegaron con su foto y su firma, y nadie las ha aprobado.',
      nivel: 'leve',
      ir: 'entregas',
      boton: 'Revisar',
    },
  ].filter((x) => x.n > 0)

  const masOMenos = h.entregado - h.entregadoAyer
  const hora = new Date().getHours()
  const saludo = hora < 12 ? 'Buenos días' : hora < 19 ? 'Buenas tardes' : 'Buenas noches'
  const nombre = yo?.nombre?.split(' ')[0]

  return (
    <section>
      <div className="tit-fila">
        <h1>{nombre ? `${saludo}, ${nombre}` : 'Cómo va la operación'}</h1>
        <span>
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
          {' · '}
          {alertas.length === 0
            ? 'no hay nada urgente'
            : alertas.length === 1
              ? '1 cosa necesita tu atención'
              : `${alertas.length} cosas necesitan tu atención`}
        </span>
      </div>

      <div className="kpis">
        <article className="kpi">
          <p className="kpi-et">En la calle</p>
          <p className="kpi-n num">{h.viajesEnCalle}</p>
          <p className="kpi-pie">{h.conductoresEnCalle} conductores</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Entregado hoy</p>
          <p className="kpi-n num">{h.entregado}</p>
          <p className="kpi-pie">
            {h.entregadoAyer === 0 ? (
              'Ayer no hubo'
            ) : masOMenos === 0 ? (
              'Igual que ayer'
            ) : (
              <>
                <span className={masOMenos > 0 ? 'sube' : 'baja'}>
                  {masOMenos > 0 ? '+' : '−'}
                  {Math.abs(masOMenos)}
                </span>{' '}
                que ayer
              </>
            )}
          </p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Sin revisar</p>
          <p className="kpi-n num">{a.sinRevisar}</p>
          <p className="kpi-pie">
            {a.corregidasEsperando > 0
              ? `Esperan aprobación · ${a.corregidasEsperando} ya corregidas`
              : 'Esperan aprobación'}
          </p>
        </article>
        {/*
          🚨🚨 EL CONDUCTOR ESTÁ QUIETO CON LA CARGA ENCIMA, ESPERANDO.
          
          Llegó, no le recibieron, y lo reportó por la app. La pelota quedó del
          lado de la oficina: hay que decirle qué hace con esa carga.
          
          MEDIDO el 24-ago-2026, con el piloto ya corriendo: **27 reportes de 31
          sin contestar**, el más viejo de hace **3 días y 18 horas**, y 10 de
          ellos con la carga todavía arriba del camión.
          
          ⛔ La pantalla para contestarlos ya existía. Lo que faltaba era que
          alguien supiera que hay que abrirla. Una pantalla que nadie sabe que
          tiene que abrir es una pantalla que no existe.
          
          Va ANTES de «Sin manifiesto» a propósito: un camión quieto con carga
          es más urgente que un papel que falta.
        */}
        <article className="kpi">
          <p className="kpi-et">Esperando respuesta</p>
          <p
            className="kpi-n num"
            style={a.esperandoRespuesta > 0 ? { color: '#CA2B2B' } : undefined}
          >
            {a.esperandoRespuesta}
          </p>
          <p className="kpi-pie">
            {a.esperandoRespuesta > 0
              ? a.esperaMasLargaHoras >= 24
                ? `Camiones quietos · el que más lleva, ${Math.floor(a.esperaMasLargaHoras / 24)} ${
                    Math.floor(a.esperaMasLargaHoras / 24) === 1 ? 'día' : 'días'
                  }`
                : `Camiones quietos · el que más lleva, ${a.esperaMasLargaHoras} h`
              : 'Nadie está esperando'}
          </p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Sin manifiesto</p>
          <p className="kpi-n num" style={a.rodandoSinManifiesto > 0 ? { color: '#CA2B2B' } : undefined}>
            {a.rodandoSinManifiesto}
          </p>
          <p className="kpi-pie">Ya van rodando</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Entregando con la app</p>
          <p className="kpi-n num">
            {h.conductoresEntregando}
            <span className="kpi-de"> / {h.conductoresEnCalle}</span>
          </p>
          {/*
           * 🚨 El número grande es el de los que ENTREGAN, no el de los que
           * entraron. Entrar una vez no es usar la app: en un piloto se
           * reparten claves, entran por curiosidad, y el termómetro se infla
           * justo cuando hay que decidir si esto sirve.
           *
           * 🚨🚨 Y AUN ASÍ ESTE NÚMERO INCLUYE LAS PRUEBAS.
           *
           * Medido el 24-ago-2026: de las 61 entregas registradas, buena parte
           * son las pruebas hechas desde las cuentas de esos conductores —al
           * mirar quién recibió salen «PRUEBA CELULAR LENTO», «PEDRO SIN
           * SENAL», «Luis luna»—. El aviso ya se puso en «Cómo va el piloto»;
           * **faltaba aquí, que es la pantalla que se abre primero cada día**.
           *
           * Un mismo número con advertencia en una pantalla y sin ella en otra
           * es peor que no tenerla en ninguna: quien mire esta se lo cree.
           */}
          {/*
            🚨 «22 ya entraron» aquí y «25 ya entraron» en «Cómo va el piloto»:
            dos pantallas, el mismo texto, números distintos. No es un error —
            este cuenta solo a los que están RODANDO hoy y aquella a todos los
            que tienen clave— pero decirlo igual obliga a adivinar cuál vale.
            Basta un «de esos» para que se entienda de una lectura.
          */}
          <p className="kpi-pie">
            conductores rodando · {h.conductoresConApp} de esos ya entraron
            <strong style={{ color: '#A4600D' }}> · incluye las pruebas</strong>
          </p>
        </article>
      </div>

      <div className="tablero">
        <div className="apilado">
          <section className="caja">
            <div className="caja-tit">
              <h2>Entregas por día</h2>
              <small>
                Últimos {datos.porDia.length} días
                {datos.porDia.length > 0 &&
                  ` · promedio ${Math.round(
                    datos.porDia.reduce((s, d) => s + d.cuantas, 0) / datos.porDia.length,
                  )} al día`}
              </small>
            </div>
            <div className="caja-cuerpo">
              <GraficaDias datos={datos.porDia} />
            </div>
          </section>

          <section className="caja">
            <div className="caja-tit">
              <h2>Dónde está la flota ahora</h2>
              <small>{datos.flota.enLaCalle} viajes rodando</small>
            </div>
            <div className="caja-cuerpo">
              <GraficaFlota f={datos.flota} />
            </div>
          </section>
        </div>

        <div className="apilado">
          <section className="caja">
            <div className="caja-tit">
              <h2>Lo que necesita tu atención</h2>
              <small>de lo más caro a lo menos</small>
            </div>
            {alertas.length === 0 ? (
              <p className="tab-vacio">Todo al día. No hay nada pendiente.</p>
            ) : (
              alertas.map((x) => (
                <article key={x.titulo} className={`alerta ${x.nivel}`}>
                  <div className="alerta-n num">{x.n}</div>
                  <div className="alerta-txt">
                    <b>{x.titulo}</b>
                    <span>{x.dice}</span>
                  </div>
                  <button onClick={() => alIrA(x.ir)} className="alerta-ir">
                    {x.boton}
                  </button>
                </article>
              ))
            )}
          </section>

          <section className="caja">
            <div className="caja-tit">
              <h2>Lo último que pasó</h2>
              <small>en vivo</small>
            </div>
            {datos.recientes.length === 0 ? (
              <p className="tab-vacio">Todavía no hay movimientos desde la app.</p>
            ) : (
              <div className="tab-movs">
                {datos.recientes.slice(0, 6).map((m, i) => {
                  const q = QUE_PASO[m.tipo] ?? { texto: m.tipo, tono: 'normal' }
                  return (
                    <div key={`${m.remesa}-${m.cuando}-${i}`} className="mov">
                      <span className={`punto p-${q.tono}`} aria-hidden="true" />
                      <span className="mov-que">{q.texto}</span>
                      <span className="mov-dato num">
                        {m.remesa}
                        {m.placa ? ` · ${m.placa}` : ''}
                      </span>
                      <span className="mov-cuando">{haceCuanto(m.cuando)}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </section>
  )
}
